import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAccessToken } from '@app/auth';
import { fetchApi, withCluster } from '@app/hooks/api';
import { CLUSTERS } from '@app/config';
import {
  Namespace, Namespaces, Resource, ResourceType,
  Autoshutdown,
} from '../models';

// ---------------------------------------------------------------------------
// Data transformation functions
// ---------------------------------------------------------------------------

function create_resource(data: any, type: ResourceType, namespace: Namespace): Resource {
  const resource = new Resource(type);
  resource.name = data.name;
  resource.namespace = namespace.name;
  resource.type = type;
  resource.status = data.status;
  resource.reason = data.reason;
  resource.description = data.description;
  if (
    typeof resource.description === 'string' &&
    ['disk_pressure', 'disk_full', 'disk_unknown', 'failing'].includes(resource.reason)
  ) {
    const trimmed = resource.description.trim();
    if (
      (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
      (trimmed.startsWith('{') && trimmed.endsWith('}'))
    ) {
      try {
        resource.description = JSON.parse(trimmed);
      } catch {
        // Keep original string when payload is not valid JSON
      }
    }
  }

  resource.deploy.commit = data.deploys?.last_deploy?.git_commit;
  resource.deploy.date = data.deploys?.last_deploy?.date
    ? new Date(data.deploys.last_deploy.date)
    : undefined;
  resource.deploy.repository = data.deploy?.repository;
  resource.helm.commit = data.deploys?.last_deploy?.git_commit_helmchart;
  resource.helm.branch = data.deploys?.last_deploy?.git_branch_helmchart;
  resource.helm.repository = data.deploys?.last_deploy?.git_url_helmchart
    ?.split('/')
    .slice(3)
    .join('/');
  resource.git.branch = data.git?.branch;

  if (data.diff?.app && Object.keys(data.diff.app).length) {
    resource.deploy.diff = new Map(Object.entries(data.diff.app));
    if (data.diff?.helm && Object.keys(data.diff.helm).length) {
      resource.helm.diff = new Map(Object.entries(data.diff.helm));
    }
  }

  if (data.deploys?.last_deploy?.git_branch) {
    resource.deploy.branch = data.deploys.last_deploy.git_branch.replace(/^origin\//, '');
  } else {
    resource.deploy.branch = undefined;
  }

  if (data.deploys?.last_deploy?.jenkins_job) {
    resource.deploy.job = data.deploys.last_deploy.jenkins_job;
  } else {
    resource.deploy.job = undefined;
  }

  resource.fast_deploy = data.fast_deploy;
  return resource;
}

function collectImageIdentifiers(resources: Resource[] = []): string {
  const identifiers = new Set<string>();
  resources.forEach((resource) => {
    [resource.deploy?.commit, resource.helm?.commit, resource.fast_deploy?.image].forEach(
      (value) => {
        if (value) identifiers.add(value);
      }
    );
  });
  return Array.from(identifiers).join(' ');
}

function processNamespacesResponse(status: any): Namespaces {
  const namespaces: Namespace[] = status.namespaces.map((data: any) => {
    const namespace = {} as Namespace;

    namespace.name = data.name;
    namespace.status = data.status;
    namespace.creator = data.creator;
    namespace.class = data.class;
    namespace.team = data.team;
    namespace.date = new Date(data.date);
    namespace.jenkins = data.jenkins;
    namespace.refresh = { branch: data.refresh.branch, link: data.refresh.link };
    namespace.branches = {
      core: data.params.BRANCH,
      cqs: data.params.CQS_BRANCH,
      ccm: data.params.CCM_BRANCH,
      mys: data.params.CCM_BRANCH,
    };
    namespace.requests = {
      count: data.requests.count,
      last_request: data.requests.last_request,
      period: data.requests.period,
    };
    namespace.idle = data.idle;
    namespace.ingresses = data.ingresses || [];
    namespace.ingressinternals = data.ingressinternals || [];
    const previewSessionOverlays = data.previewSessionOverlays || [];
    namespace.previewSessionOverlays = previewSessionOverlays;
    namespace.resources = [];

    // The synthetic Deployments mirrord creates per preview session (e.g.
    // deployment-admin-preview-test-<hash>) are real k8s resources, but they're transient and
    // already managed through PreviewSessionsCard (with its own Stop action) — keep them out of
    // the main Resources table so they don't clutter it or show as stale/"Outdated".
    const overlayWorkloadNames = new Set(
      previewSessionOverlays.flatMap((s: any) => (s.workloads || []).map((w: any) => w.name).filter(Boolean))
    );

    const deployments = data.deployments
      ?.filter((d: any) => !overlayWorkloadNames.has(d.name))
      .map((d: any) => create_resource(d, ResourceType.Deployment, namespace)) ?? [];
    const statefulsets = data.statefulsets
      ?.filter((s: any) => !overlayWorkloadNames.has(s.name))
      .map((s: any) => create_resource(s, ResourceType.Statefulset, namespace)) ?? [];

    namespace.resources = [...deployments, ...statefulsets].sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    const imageIdentifiers = collectImageIdentifiers(namespace.resources);
    namespace.commit = imageIdentifiers;
    namespace.image = imageIdentifiers;

    return namespace;
  });

  return { date: status.date, items: namespaces };
}

// ---------------------------------------------------------------------------
// Metadata computation (moved from Dashboard.tsx)
// ---------------------------------------------------------------------------

export interface MetadataNamespace {
  running: number;
  stopped: number;
  updating: number;
  outdated: number;
  stale: number;
  warning: number;
  patching: number;
  failing: number;
  unknown: number;
  total: number;
}

export interface Metadata {
  namespaces: MetadataNamespace;
}

export interface AutoshutdownStatus {
  success: number;
  failure: number;
  total: number;
}

function computeMetadata(namespaces: Namespace[]): Metadata {
  return {
    namespaces: {
      running: namespaces.filter((n) => n.status === 'running').length,
      stale: namespaces.filter((n) => n.status === 'stale').length,
      outdated: namespaces.filter((n) => n.status === 'outdated').length,
      stopped: namespaces.filter((n) => n.status === 'stopped').length,
      updating: namespaces.filter((n) => n.status === 'updating').length,
      patching: namespaces.filter((n) => n.status === 'patching').length,
      warning: namespaces.filter((n) => n.status === 'warning').length,
      failing: namespaces.filter((n) => n.status === 'failing').length,
      unknown: namespaces.filter((n) => n.status === 'unknown').length,
      total: namespaces.length,
    },
  };
}

// ---------------------------------------------------------------------------
// React Query Hooks
// ---------------------------------------------------------------------------

const clusterKey = () => globalThis.cluster_selected_value;

/**
 * Fetches namespaces with 60s polling. Derives metadata via useMemo.
 */
export function useNamespaces(refetchEnabled = true) {
  const getToken = useAccessToken();

  const query = useQuery<Namespaces>({
    queryKey: ['dashboard', 'namespaces', clusterKey()],
    queryFn: () => fetchApi(withCluster('/api/namespaces.json', clusterKey()), getToken).then(processNamespacesResponse),
    staleTime: 30 * 1000,
    refetchInterval: refetchEnabled ? 60 * 1000 : false,
    retry: 3,
    retryDelay: 2000,
  });

  const namespaces = query.data?.items ?? [];
  const lastUpdate = query.data?.date;

  const metadata = useMemo(() => computeMetadata(namespaces), [namespaces]);

  return {
    namespaces,
    lastUpdate,
    metadata,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? (query.error as Error).message : null,
  };
}

/**
 * Fetches autoshutdown status.
 */
export function useAutoshutdown() {
  const getToken = useAccessToken();

  const query = useQuery<Autoshutdown[]>({
    queryKey: ['dashboard', 'autoshutdown', clusterKey()],
    queryFn: async () => {
      const response = await fetchApi(withCluster('/api/autoshutdown.json', clusterKey()), getToken);
      return response.autoshutdown as Autoshutdown[];
    },
    staleTime: 5 * 60 * 1000,
    retry: 3,
    retryDelay: 2000,
  });

  const autoshutdownList = query.data ?? [];

  const autoshutdowndata = useMemo<AutoshutdownStatus>(() => ({
    success: autoshutdownList.filter((a) => a.result === 'SUCCESS').length,
    failure: autoshutdownList.filter((a) => a.result === 'FAILURE').length,
    total: autoshutdownList.length,
  }), [autoshutdownList]);

  return { autoshutdowndata, isLoading: query.isLoading, error: query.error };
}


/**
 * Fetches quick deploy permission and stores in localStorage.
 */
export function useQuickDeployPermission() {
  const getToken = useAccessToken();

  useQuery<boolean>({
    queryKey: ['dashboard', 'quickDeployPermission', clusterKey()],
    queryFn: async () => {
      const response = await fetchApi(withCluster('/api/quickdeploy/authorized', clusterKey()), getToken);
      const enabled = response.enabled === true;
      localStorage.setItem('quick_deploy_enabled', enabled ? 'true' : 'false');
      const allowed: string[] | null = Array.isArray(response.allowedClusters) ? response.allowedClusters : null;
      const previous = localStorage.getItem('allowed_clusters');
      const current = allowed ? JSON.stringify(allowed) : null;
      if (current) localStorage.setItem('allowed_clusters', current);
      else localStorage.removeItem('allowed_clusters');
      // First detection or a change: move restricted users onto their team's cluster and re-render pickers.
      if (current !== previous) {
        if (allowed && !allowed.includes(clusterKey())) {
          const target = Object.values(CLUSTERS).map(c => c.CLUSTER_NAME).find(n => allowed.includes(n));
          if (target) localStorage.setItem('cluster_selected', target);
        }
        window.location.reload();
      }
      return enabled;
    },
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
}

